/**
 * IterationList Component
 *
 * Displays a list of task iterations with actions to create new iterations,
 * view reports, and manage iteration lifecycle.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskIteration, IterationAttachment, IterationStatus } from '@/types/tasks';
import { useStatusWebSocket } from '@/hooks/useStatusWebSocket';
import {
  getTaskIterations,
  submitIteration,
  cancelIteration,
  deleteIteration,
  markIterationsRead,
  getIterationAttachmentDownloadUrl,
  rateIteration,
  suggestRevisionPrompt,
} from '@/services/api';
import IterationCard from './IterationCard';
import NewIterationModal from './NewIterationModal';

interface IterationListProps {
  taskId: string;
  hasUnreadIterations?: boolean;
  onNewIteration?: () => void;
  onEditIteration?: (iteration: TaskIteration) => void;
  onCompleteIteration?: (iteration: TaskIteration) => void;
  onViewReport?: (iteration: TaskIteration) => void;
  onIterationsChange?: () => void;
}

export default function IterationList({
  taskId,
  hasUnreadIterations = false,
  onNewIteration,
  onEditIteration,
  onCompleteIteration,
  onViewReport,
  onIterationsChange,
}: IterationListProps) {
  const { t } = useTranslation('tasks');
  // Real-time status WebSocket
  const { lastIterationUpdate } = useStatusWebSocket();

  const [iterations, setIterations] = useState<TaskIteration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track if we've already marked as read to prevent loops
  const [hasMarkedRead, setHasMarkedRead] = useState(false);

  const loadIterations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getTaskIterations(taskId, true);
      setIterations(response.iterations);
    } catch (err) {
      console.error('Failed to load iterations:', err);
      setError('Failed to load iterations');
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  // Initial load - only depends on taskId
  useEffect(() => {
    loadIterations();
  }, [taskId, loadIterations]);

  // Separate effect for marking as read - only runs once when unread
  // Note: We don't call onIterationsChange after mark-read to avoid infinite loops.
  // The hasMarkedRead state handles hiding the "unread" badge locally.
  useEffect(() => {
    if (hasUnreadIterations && !hasMarkedRead && !isLoading) {
      setHasMarkedRead(true);
      markIterationsRead(taskId).catch(err => {
        console.error('Failed to mark iterations as read:', err);
      });
    }
  }, [hasUnreadIterations, hasMarkedRead, isLoading, taskId]);

  // React to real-time iteration status updates from WebSocket
  useEffect(() => {
    if (lastIterationUpdate && lastIterationUpdate.task_id.toUpperCase() === taskId.toUpperCase()) {
      // If iteration completed, refetch to get output attachments
      if (lastIterationUpdate.status === 'completed') {
        loadIterations();
        onIterationsChange?.();
      } else {
        // For other status changes, just update the status locally
        setIterations(prev => prev.map(it =>
          it.id === lastIterationUpdate.id
            ? { ...it, status: lastIterationUpdate.status as IterationStatus }
            : it
        ));
      }
    }
  }, [lastIterationUpdate, taskId, loadIterations, onIterationsChange]);

  const handleSubmit = async (iteration: TaskIteration) => {
    try {
      setActionLoading(iteration.id);
      await submitIteration(taskId, iteration.id);
      await loadIterations();
      onIterationsChange?.();
    } catch (err) {
      console.error('Failed to submit iteration:', err);
      alert('Failed to submit iteration. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (iteration: TaskIteration) => {
    if (!confirm('Are you sure you want to cancel this iteration?')) return;

    try {
      setActionLoading(iteration.id);
      await cancelIteration(taskId, iteration.id);
      await loadIterations();
      onIterationsChange?.();
    } catch (err) {
      console.error('Failed to cancel iteration:', err);
      alert('Failed to cancel iteration. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (iteration: TaskIteration) => {
    if (!confirm('Are you sure you want to delete this iteration? This cannot be undone.')) return;

    try {
      setActionLoading(iteration.id);
      await deleteIteration(taskId, iteration.id);
      await loadIterations();
      onIterationsChange?.();
    } catch (err) {
      console.error('Failed to delete iteration:', err);
      alert('Failed to delete iteration. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadAttachment = async (attachment: IterationAttachment) => {
    try {
      const { url } = await getIterationAttachmentDownloadUrl(
        taskId,
        attachment.iteration_id || '',
        attachment.id
      );
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  // Rating state
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionInitialPrompt, setRevisionInitialPrompt] = useState<string | undefined>(undefined);
  const [ratingFeedback, setRatingFeedback] = useState<string | undefined>(undefined);
  const [ratingIterationId, setRatingIterationId] = useState<string | null>(null);

  const handleRate = async (iteration: TaskIteration, stars: number, feedback?: string) => {
    try {
      // Optimistic update — set rating locally before API call
      const ratedAt = new Date().toISOString();
      setIterations(prev => prev.map(it =>
        it.id === iteration.id
          ? { ...it, rating_stars: stars, rating_feedback: feedback ?? null, rated_at: ratedAt }
          : it
      ));

      await rateIteration(taskId, iteration.id, stars, feedback);
      // Store feedback for potential revision prompt
      if (feedback) {
        setRatingFeedback(feedback);
        setRatingIterationId(iteration.id);
      } else {
        setRatingFeedback(undefined);
        setRatingIterationId(null);
      }
    } catch (err) {
      console.error('Failed to rate iteration:', err);
      // Revert optimistic update on failure
      setIterations(prev => prev.map(it =>
        it.id === iteration.id
          ? { ...it, rating_stars: iteration.rating_stars, rating_feedback: iteration.rating_feedback, rated_at: iteration.rated_at }
          : it
      ));
      alert('Failed to rate iteration. Please try again.');
    }
  };

  const handleRequestRevision = async () => {
    // If we have feedback from the rating, generate a suggested prompt
    if (ratingFeedback && ratingIterationId) {
      try {
        const result = await suggestRevisionPrompt(taskId, ratingIterationId, ratingFeedback);
        setRevisionInitialPrompt(result.suggested_prompt);
      } catch (err) {
        console.error('Failed to generate revision prompt:', err);
        // Open modal without suggestion on error
        setRevisionInitialPrompt(undefined);
      }
    } else {
      setRevisionInitialPrompt(undefined);
    }
    setShowRevisionModal(true);
  };

  // Group iterations by status for summary
  const statusCounts = iterations.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Check if we can add new iterations (after iteration #1 is completed OR failed)
  // Task itself IS iteration #1, so "New Iteration" = "Request Revision"
  // Allow revision when the task has been processed (success or failure)
  const hasProcessedIteration = iterations.some(it => it.status === 'completed' || it.status === 'failed');
  const canAddNewIteration = hasProcessedIteration;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{t('iterations.sectionTitle')}</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <svg className="w-6 h-6 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="ml-2 text-gray-600">{t('iterations.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('iterations.title')}
            {iterations.length > 1 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                {t('iterations.count', { count: iterations.length })}
              </span>
            )}
          </h3>
          {hasUnreadIterations && !hasMarkedRead && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
              {t('iterations.new')}
            </span>
          )}
        </div>
        {/* Only show "Request Revision" after first iteration is completed */}
        {canAddNewIteration && (
          <button
            onClick={onNewIteration}
            className="px-3 py-1.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('iterations.requestRevision')}
          </button>
        )}
      </div>

      {/* Status Summary */}
      {iterations.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {statusCounts.completed && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
              {t('iterations.statusSummary.completed', { count: statusCounts.completed })}
            </span>
          )}
          {statusCounts.processing && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full animate-pulse">
              {t('iterations.statusSummary.processing', { count: statusCounts.processing })}
            </span>
          )}
          {statusCounts.pending && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
              {t('iterations.statusSummary.pending', { count: statusCounts.pending })}
            </span>
          )}
          {statusCounts.draft && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
              {t('iterations.statusSummary.draft', { count: statusCounts.draft })}
            </span>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
          <button
            onClick={loadIterations}
            className="ml-2 text-red-800 underline hover:no-underline"
          >
            {t('iterations.error.retry')}
          </button>
        </div>
      )}

      {/* Empty State - should not normally appear as iteration #1 is auto-created */}
      {iterations.length === 0 && !error && (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h4 className="mt-2 text-sm font-medium text-gray-900">{t('iterations.empty.title')}</h4>
          <p className="mt-1 text-sm text-gray-500">
            {t('iterations.empty.description')}
          </p>
        </div>
      )}

      {/* Iterations List */}
      {iterations.length > 0 && (
        <div className="space-y-3">
          {iterations.map((iteration) => (
            <IterationCard
              key={iteration.id}
              taskId={taskId}
              iteration={iteration}
              onViewReport={onViewReport}
              onEdit={onEditIteration}
              onSubmit={handleSubmit}
              onComplete={onCompleteIteration}
              onCancel={handleCancel}
              onDelete={handleDelete}
              onDownloadAttachment={handleDownloadAttachment}
              onRate={handleRate}
              onRequestRevision={handleRequestRevision}
              isLoading={actionLoading === iteration.id}
            />
          ))}
        </div>
      )}

      {/* Revision modal triggered by rating feedback */}
      <NewIterationModal
        taskId={taskId}
        isOpen={showRevisionModal}
        onClose={() => {
          setShowRevisionModal(false);
          setRevisionInitialPrompt(undefined);
          setRatingFeedback(undefined);
          setRatingIterationId(null);
        }}
        onCreated={() => {
          setShowRevisionModal(false);
          setRevisionInitialPrompt(undefined);
          setRatingFeedback(undefined);
          setRatingIterationId(null);
          loadIterations();
          onIterationsChange?.();
        }}
        initialPrompt={revisionInitialPrompt}
      />
    </div>
  );
}
